import { useFocusEffect } from 'expo-router';
import {
  SavingsRates,
  toSavingsRates,
} from 'features/accounts/helpers/savingsRates.helpers';
import { fetchSavingsRates } from 'features/v2a/receiver/helpers/Payments';
import { useContext, useEffect, useRef, useState } from 'react';
import { useAppLifecycle } from 'shared/hooks/useAppLifecycle/useAppLifecycle';
import useEvent from 'shared/hooks/useEvent/useEvent';
import { AuthContext, AuthContextProps } from 'shared/providers/auth';
import {
  EnvironmentContext,
  EnvironmentContextProps,
} from 'shared/providers/environment';
import { Environment } from 'shared/providers/environment.constants';
import { logBreadcrumb } from 'shared/utils/sentry/sentry';

interface UseReceiverSavingsRatesResult {
  rates: SavingsRates | undefined;
  fetching: boolean;
  isUnavailable: boolean;
  refetch: () => void;
}

export const useReceiverSavingsRates = (): UseReceiverSavingsRatesResult => {
  const { currentEnv } =
    useContext<EnvironmentContextProps>(EnvironmentContext);
  const { getVaroSessionId } = useContext<AuthContextProps>(AuthContext);

  const { lastResumeAt } = useAppLifecycle();
  const [rates, setRates] = useState<SavingsRates>();
  const [reloadKey, setReloadKey] = useState(0);
  const [settledRequestKey, setSettledRequestKey] = useState<string>();
  const requestKey = `${currentEnv}:${reloadKey}:${lastResumeAt ?? 0}`;

  const refetch = () => setReloadKey((key) => key + 1);

  const requestSavingsRates = useEvent(
    async (env: Environment, signal: AbortSignal) => {
      const varoSessionId = await getVaroSessionId();
      return fetchSavingsRates(env, varoSessionId, signal);
    },
  );

  useEffect(() => {
    const abortController = new AbortController();

    const load = async () => {
      try {
        const data = await requestSavingsRates(
          currentEnv,
          abortController.signal,
        );
        if (abortController.signal.aborted) {
          return;
        }
        const loadedRates = toSavingsRates(data, new Date());
        if (!loadedRates) {
          logBreadcrumb({
            level: 'error',
            category: 'V2A savings rates',
            message: 'Savings rates response was incomplete',
          });
        } else {
          setRates(loadedRates);
        }
      } catch (error) {
        if (abortController.signal.aborted) {
          return;
        }
        logBreadcrumb({
          level: 'error',
          category: 'V2A savings rates',
          message: 'Savings rates request failed; keeping last loaded values',
          data: {
            error: error instanceof Error ? error.message : String(error),
          },
        });
      }
      setSettledRequestKey(requestKey);
    };
    void load();

    return () => {
      abortController.abort();
    };
  }, [currentEnv, requestKey, requestSavingsRates]);

  const hasFocusedOnce = useRef(false);
  const refetchOnReturn = useEvent(() => {
    if (hasFocusedOnce.current) {
      setReloadKey((key) => key + 1);
    }
    hasFocusedOnce.current = true;
  });
  useFocusEffect(refetchOnReturn);

  const fetching = settledRequestKey !== requestKey;

  return {
    rates,
    fetching,
    isUnavailable: !fetching && !rates,
    refetch,
  };
};
